import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, apiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { OtpStep } from "@/components/OtpStep";
import { Truck, SignIn, ChartLineUp, ShieldCheck, Lightning } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [challenge, setChallenge] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data.otp_required) setChallenge(data);
      else navigate("/");
    } catch (err) {
      if (err.response?.data?.detail === "first_login") { navigate("/carrier-setup"); return; }
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white">
      <div className="w-full lg:w-[480px] flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2.5 mb-10">
            <img src="/freightx-logo.webp" alt="Asterism Services" className="w-10 h-10 object-contain" />
            <div className="leading-none">
              <span className="font-heading font-extrabold text-xl text-slate-900 block">Asterism Services</span>
              <span className="text-[11px] text-blue-500 font-semibold">Built with Love! ❤</span>
            </div>
          </div>
          {challenge ? <OtpStep challenge={challenge} onBack={() => { setChallenge(null); setPassword(""); }} onSuccess={() => navigate("/")} /> : (<>
          <h1 className="font-heading font-extrabold text-3xl text-slate-900">Welcome Back</h1>
          <p className="text-sm text-slate-500 mt-2 mb-8">Sign in to your transportation management workspace.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-600">Email</label>
              <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" required
                className="bg-white border-slate-300 focus:border-blue-500 focus-visible:ring-blue-500 h-11" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-600">Password</label>
              <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required
                className="bg-white border-slate-300 focus:border-blue-500 focus-visible:ring-blue-500 h-11" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
            <Button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full h-11 bg-blue-600 text-white hover:bg-blue-700 font-semibold">
              <SignIn size={18} weight="bold" className="mr-2" />
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <div className="mt-6 text-center">
            <Link to="/forgot-password" className="text-sm text-slate-500 hover:text-blue-600 transition-colors" data-testid="forgot-link">
              Forgot your password?
            </Link>
            <Link to="/carrier-setup" className="block mt-2 text-sm text-blue-600 hover:text-blue-700 font-semibold" data-testid="carrier-first-login-link">
              Carrier? First-Time Login
            </Link>
          </div>
          </>)}
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-blue-600 relative overflow-hidden items-center justify-center p-16">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, white 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        <div className="relative z-10 text-white max-w-lg">
          <div className="rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/20 mb-8">
            <img src="/exec-portrait.png" alt="FreightCommand leadership" className="w-full h-auto object-cover" data-testid="login-portrait" />
          </div>
          <h2 className="font-heading font-extrabold text-3xl leading-tight">The all-in-one TMS built to move freight, not just data.</h2>
          <p className="text-blue-100 mt-3">Dispatch, fleet, drivers, safety, accounting and analytics — one platform for your whole operation.</p>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-3">
            {[
              { icon: Lightning, t: "Dispatch to invoice" },
              { icon: ChartLineUp, t: "Live P&L clarity" },
              { icon: ShieldCheck, t: "Compliance alerts" },
            ].map((f, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="p-1.5 rounded-lg bg-white/15"><f.icon size={16} weight="duotone" /></div>
                <p className="text-sm font-medium">{f.t}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
